import { useQuery } from '@tanstack/react-query'
import { api } from '@/services'

/** Raw cleaning display info from the API */
interface CleaningDisplayRaw {
  /** ISO timestamp (RFC3339) */
  timestamp: string
  /** Server-formatted date (UTC - do not use) */
  date: string
  /** Server-formatted time (UTC - do not use) */
  time: string
}

/** Cleaning display info with browser-local formatting */
export interface CleaningDisplay {
  /** ISO timestamp */
  timestamp: string
  /** Date formatted in the browser's local timezone */
  date: string
  /** Time formatted in the browser's local timezone */
  time: string
}

/** Format a CleaningDisplayRaw into browser-local timezone strings */
function toLocalCleaning(raw: CleaningDisplayRaw): CleaningDisplay {
  const d = new Date(raw.timestamp)
  if (isNaN(d.getTime())) {
    return { timestamp: raw.timestamp, date: raw.date, time: raw.time }
  }
  return {
    timestamp: raw.timestamp,
    date: d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    time: d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }),
  }
}

/** Branding info for public display */
export interface PublicBranding {
  org_name: string
  logo_url?: string
  primary_color?: string
  secondary_color?: string
  /**
   * IANA timezone name for the organization (e.g. "America/Toronto").
   * Exposed on the public response so on-site viewers see the building's canonical tz.
   */
  timezone?: string
}

/** Raw API response (dates formatted server-side in UTC) */
interface PublicRoomDisplayRaw {
  room_name: string
  building_name?: string
  last_cleaning?: CleaningDisplayRaw
  previous_cleaning?: CleaningDisplayRaw
  branding?: PublicBranding
}

/** Response from the public room display endpoint */
export interface PublicRoomDisplayData {
  room_name: string
  building_name?: string
  last_cleaning?: CleaningDisplay
  previous_cleaning?: CleaningDisplay
  branding?: PublicBranding
}

/** Convert raw API response to use browser-local timezone formatting */
function toLocalDisplay(raw: PublicRoomDisplayRaw): PublicRoomDisplayData {
  return {
    ...raw,
    last_cleaning: raw.last_cleaning
      ? toLocalCleaning(raw.last_cleaning)
      : undefined,
    previous_cleaning: raw.previous_cleaning
      ? toLocalCleaning(raw.previous_cleaning)
      : undefined,
  }
}

/**
 * Hook to fetch public room display data.
 * This endpoint does not require authentication.
 * Dates are converted from UTC to the browser's local timezone.
 */
export function usePublicRoomDisplay(roomId: string) {
  return useQuery({
    queryKey: ['public', 'rooms', roomId, 'display'] as const,
    queryFn: async () => {
      const raw = await api.get<PublicRoomDisplayRaw>(
        `/public/rooms/${roomId}/display`,
        { noAuth: true },
      )
      return toLocalDisplay(raw)
    },
    enabled: !!roomId,
    // Public data can be cached longer since it's read-only
    staleTime: 1000 * 60, // 1 minute
  })
}
